import React from 'react';

import { AddressDeliveryIntervals, AddressSearch } from 'components/address';
import { Button, Image } from 'components/ui';
import IconMapPin from 'components/ui/icons/IconMapPin';
import IconTruck from 'components/ui/icons/IconTruck';
import { HOLIDAY_ICONS } from 'constants/seasonal';
import { useDeliveryInfoDispatchHelpers } from 'context/DeliveryInfoContext';
import { getActiveHoliday } from 'helpers/SeasonalHelpers';
import { useHeaderDeliveryAddress } from 'hooks/useHeaderDeliveryAddress';
import { useHeaderDeliveryInterval } from 'hooks/useHeaderDeliveryInterval';

import { Holiday } from 'types/holidays';

import type { IconComponent } from 'types';

import styles from './HeaderDesktop.module.scss';

const IconTypes = {
  Pin: 'pin',
  Interval: 'interval',
};

type IconType = typeof IconTypes[keyof typeof IconTypes];

const DeliveryButtons = () => {
  const {
    label: deliveryAddressLabel, token: deliveryAddressToken, isAuthenticated, isPlaceholder
  } = useHeaderDeliveryAddress();
  const { label: deliveryIntervalLabel } = useHeaderDeliveryInterval(deliveryAddressToken, !!isAuthenticated);
  const { showDeliveryInfoModal } = useDeliveryInfoDispatchHelpers();

  const onAddressClick = React.useCallback(() => {
    showDeliveryInfoModal(<AddressSearch />);
  }, [showDeliveryInfoModal]);

  const onIntervalClick = () => {
    if (isPlaceholder) {
      showDeliveryInfoModal(<AddressSearch />);
    } else {
      showDeliveryInfoModal(<AddressDeliveryIntervals />);
    }
  };

  return (
    <>
      <Button
        size="medium"
        className={styles.deliveryButton}
        beforeContent={<MapPinIcon />}
        onClick={onAddressClick}
        data-testid="header.delivery-address"
      >
        <span className={styles.deliveryAddress}>
          { deliveryAddressLabel }
        </span>
      </Button>

      <Button
        size="medium"
        className={styles.deliveryButton}
        beforeContent={<DeliveryIntervalIcon />}
        onClick={onIntervalClick}
        data-testid="header.delivery-intervals"
      >
        <span className={styles.deliveryInterval}>
          {deliveryIntervalLabel}
        </span>
      </Button>
    </>
  );
};

const HeaderDeliveryIcon = ({ iconType, defaultIcon: Icon }: { iconType: IconType, defaultIcon: IconComponent }) => {
  const holiday = getActiveHoliday();

  if (holiday) {
    const imageSrc = getHolidayImage(iconType, holiday);

    const isChristmasSleigh = holiday === Holiday.Christmas && iconType === IconTypes.Pin;

    if (imageSrc) {
      return (
        <Image
          className={styles.deliveryButtonIcon}
          height={22}
          width={isChristmasSleigh ? 30 : 22}
          src={imageSrc}
        />
      );
    }
  }

  return <Icon size={22} className={styles.deliveryButtonIcon} />;
};

const MapPinIcon = () => (
  <HeaderDeliveryIcon iconType={IconTypes.Pin} defaultIcon={IconMapPin} />
);

const DeliveryIntervalIcon = () => (
  <HeaderDeliveryIcon iconType={IconTypes.Interval} defaultIcon={IconTruck} />
);

function getHolidayImage(type: IconType, holiday: Holiday): string | null {
  const holidayImages = {
    [IconTypes.Pin]: {
      [Holiday.Christmas]: HOLIDAY_ICONS.DELIVERY_MAP_PIN_DESKTOP.CHRISTMAS,
    },
    [IconTypes.Interval]: {
      [Holiday.Easter]: HOLIDAY_ICONS.DELIVERY_INTERVAL_DESKTOP.EASTER,
      [Holiday.Christmas]: HOLIDAY_ICONS.DELIVERY_INTERVAL_DESKTOP.CHRISTMAS,
    },
  };

  return holidayImages[type]?.[holiday] || null;
}

export default DeliveryButtons;
